import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { verifyResetCode, resendForgetCode } from '../../redux/actions/forgetPasswordAction';
import { resetForgetPasswordState } from '../../redux/slices/forgetPasswordSlice';
import { useDarkMode } from '../../App';
import { motion } from 'framer-motion';
import { FiMail, FiRefreshCw, FiArrowLeft, FiSmartphone, FiShield, FiGlobe } from 'react-icons/fi';
import '../AuthShared.css';

const LanguageSwitcher = () => {
    const { i18n } = useTranslation('global');
    const isAR = i18n.language === 'ar';

    const toggleLanguage = () => {
        const newLang = isAR ? 'en' : 'ar';
        i18n.changeLanguage(newLang);
        localStorage.setItem('lang', newLang);
    };

    return (
        <motion.button
            className="auth-lang-switcher"
            onClick={toggleLanguage}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
        >
            <FiGlobe />
            <span>{isAR ? 'English' : 'العربية'}</span>
        </motion.button>
    );
};

const OTPForget = () => {
  const { t, i18n } = useTranslation('global');
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const forgetPasswordState = useSelector((state) => state.forgetPassword);
  const { isLoading, success, error, codeVerified, resendLoading, resendSuccess } = forgetPasswordState || {};

  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t('otpForget.title', 'Security Verification');
  }, [t]);

  useEffect(() => {
    if (codeVerified && success) {
      toast.success(t('otpForget.successMessage', 'Code verified successfully!'));
      dispatch(resetForgetPasswordState());
      setTimeout(() => navigate('/reset-password', { state: { email, code } }), 800);
    }
  }, [codeVerified, success, t, navigate, dispatch, email, code]);

  useEffect(() => {
    if (resendSuccess) {
      toast.success(t('otpForget.resendSuccess', 'Code resent successfully!'));
    }
  }, [resendSuccess, t]);

  useEffect(() => {
    if (error) {
      toast(error);
      dispatch(resetForgetPasswordState());
    }
  }, [error, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetForgetPasswordState());
    };
  }, [dispatch]);

  const handleCodeInput = (value) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 6);
    setCode(sanitized);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (!/^\d+$/.test(pastedData)) return;
    const sanitized = pastedData.replace(/\D/g, '').slice(0, 6);
    setCode(sanitized);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!email) {
      toast(t('otpForget.emailRequired', 'Email is required'));
      return;
    }
    if (code.length !== 6) {
      toast(t('otpForget.enterDigits', 'Please enter all 6 digits'));
      return;
    }
    dispatch(verifyResetCode({ email, code, lang: i18n.language }));
  };

  const handleResendCode = () => {
    if (!email) {
      toast(t('otpForget.emailRequired', 'Email is required'));
      return;
    }
    dispatch(resendForgetCode({ email, lang: i18n.language }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div className={`auth-page ${isDarkMode ? 'dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-bg-overlay"></div>
      <div className="auth-blobs">
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
      </div>

      <LanguageSwitcher />

      <motion.div 
        className="auth-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="auth-card" variants={itemVariants}>
          <div className="auth-brand">
            <motion.div className="auth-brand-circle" whileHover={{ scale: 1.05, rotate: 5 }}>
              <FiShield style={{ fontSize: '24px', color: '#fff' }} />
            </motion.div>
            <h1>{t('otpForget.brandTitle', 'Protected Reset')}</h1>
          </div>

          <div className="auth-header">
            <h2>{t('otpForget.title', 'Reset Verification')}</h2>
            <p>{t('otpForget.subtitle', 'Enter the security code to authorize your password reset')}</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <label htmlFor="email"><FiMail /> {t('otpForget.email', 'Email Address')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@mail.com"
                className={submitted && !email ? 'error' : ''}
              />
            </div>

            <div className="auth-input-group">
              <label className="auth-label"><FiSmartphone /> {t('otpForget.code', 'Reset Code')}</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength="6"
                value={code}
                onChange={(e) => handleCodeInput(e.target.value)}
                onPaste={handlePaste}
                placeholder="000000"
                style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '20px', fontWeight: 'bold' }}
                className={submitted && code.length < 6 ? 'error' : ''}
              />
            </div>

            <motion.button 
              type="submit" 
              className="auth-submit-btn" 
              disabled={isLoading}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? <span className="auth-spinner"></span> : t('otpForget.verify', 'Verify Code')}
            </motion.button>

            <div className="auth-footer" style={{ marginTop: '10px' }}>
              <p>{t('otpForget.didntReceive', "Didn't receive code?")}</p>
              <button
                type="button"
                className="auth-link"
                style={{ background: 'none', border: 'none', padding: 0 }}
                onClick={handleResendCode}
                disabled={resendLoading}
              >
                {resendLoading ? t('otpForget.resending', 'Sending...') : (
                  <>
                    <FiRefreshCw style={{ verticalAlign: 'middle', marginRight: '5px' }} />
                    {t('otpForget.resendCode', 'Resend Code')}
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="auth-footer">
            <button className="auth-back-btn" onClick={() => navigate('/login')}>
              <FiArrowLeft style={{ verticalAlign: 'middle', marginRight: '5px' }} />
              {t('otpForget.backToLogin', 'Back to Login')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default OTPForget;

